import { z } from "zod";
import client from "@/Graphql/apolloClient";
import { ApolloError } from "@apollo/client";
import { 
  AUTHENTICATE_MUTATION, 
  USER_ACCOUNT_QUERY,
  REGISTER_MUTATION,
  RESET_PASSWORD_FINISH
} from "@/Graphql/AuthQueries";
import { generateMockData } from "@/Graphql/MockData";

// Handle GraphQL errors
const handleGraphQLError = (error: ApolloError | Error): never => {
  // Handle unauthorized errors
  if (error instanceof ApolloError && error.graphQLErrors.some(err => err.extensions?.code === 'UNAUTHENTICATED')) {
    authStorage.removeToken();
    authStorage.removeUser();
  }
  
  let errorMessage = 'An error occurred';
  if (error instanceof ApolloError) {
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      errorMessage = error.graphQLErrors[0].message;
    } else if (error.networkError) {
      errorMessage = 'Network error. Please check your connection.';
    }
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }
  
  throw new Error(errorMessage);
}

// Define schemas directly in this file
const loginSchema = z.object({
  email: z.string().min(1, "Username or email is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const registerSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  email: z.string().email("Invalid email format"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email format"),
});

const resetPasswordSchema = z.object({
  token: z.string(),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export type LoginData = z.infer<typeof loginSchema>;
export type RegisterData = z.infer<typeof registerSchema>;
export type ForgotPasswordData = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordData = z.infer<typeof resetPasswordSchema>;

// Authentication API functions
export const authApi = {
  login: async (data: LoginData) => {
    try {
      const companyId = import.meta.env.VITE_APP_COMPANY_ID;
      const concatenatedUsername = `${data.email}:${companyId}`;
      
      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        const mockData = await generateMockData(AUTHENTICATE_MUTATION);
        return mockData.data;
      }
      
      const response = await client.mutate({
        mutation: AUTHENTICATE_MUTATION,
        variables: {
          username: concatenatedUsername,
          password: data.password,
        },
      });
      
      if (!response.data || !response.data.authenticate || !response.data.authenticate.authData.accessToken) {
        throw new Error("Invalid username or password");
      }
      
      // Store the token
      const token = response.data.authenticate.authData.accessToken;
      authStorage.setToken(token);
      
      // Fetch user details
      return await authApi.getCurrentUser();
    } catch (error: any) {
      let errorMsg = "Login failed";
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMsg = error.graphQLErrors[0].message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      throw new Error(errorMsg);
    }
  },
  
  register: async (data: RegisterData) => {
    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        const mockData = await generateMockData(REGISTER_MUTATION);
        return mockData.data;
      }
      
      const response = await client.mutate({
        mutation: REGISTER_MUTATION,
        variables: {
          username: data.username,
          email: data.email,
          password: data.password,
        },
      });
      
      if (!response.data || !response.data.register) {
        throw new Error("Registration failed");
      }
      
      return response.data;
    } catch (error: any) {
      let errorMsg = "Registration failed";
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMsg = error.graphQLErrors[0].message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      throw new Error(errorMsg);
    }
  },
  
  logout: async () => {
    // GraphQL doesn't typically need a logout endpoint; just clear local storage
    authStorage.removeToken();
    authStorage.removeUser();
    return { success: true };
  },
  
  getCurrentUser: async () => {
    try {
      const token = authStorage.getToken();
      if (!token) {
        throw new Error("Not authenticated");
      }
      
      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        const mockData = await generateMockData(USER_ACCOUNT_QUERY);
        const userData = mockData.data.userAccount[0];
        authStorage.setUser(userData);
        return userData;
      }
      
      const { data } = await client.query({
        query: USER_ACCOUNT_QUERY,
        variables: { accessToken: token },
        fetchPolicy: 'network-only' // Don't use cache for user data
      });
      
      if (!data || !data.userAccount || !data.userAccount[0]) {
        throw new Error("Failed to fetch user data");
      }
      
      const userData = data.userAccount[0];
      authStorage.setUser(userData);
      return userData;
    } catch (error: any) {
      // If there's an error fetching the user, clear auth data
      authStorage.removeToken();
      authStorage.removeUser();
      
      let errorMsg = "Failed to fetch user data";
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMsg = error.graphQLErrors[0].message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      throw new Error(errorMsg);
    }
  },
  
  // For these functions, you'll need to create the appropriate GraphQL mutations
  // and add them to your Queries file
  forgotPassword: async (data: ForgotPasswordData) => {
    // Placeholder - replace with actual GraphQL mutation when available
    console.warn("forgotPassword: GraphQL mutation not implemented yet");
    throw new Error("Not implemented in GraphQL yet");
    // When implemented, will look something like:
    // const response = await client.mutate({
    //   mutation: FORGOT_PASSWORD_MUTATION,
    //   variables: { email: data.email },
    // });
    // return response.data;
  },
  
  resetPassword: async (data: ResetPasswordData) => {
    try {
      const response = await client.mutate({
        mutation: RESET_PASSWORD_FINISH,
        variables: { 
          key: data.token, // The token is actually the key from the URL
          newPassword: data.password 
        },
      });
      
      if (!response.data || !response.data.resetPasswordFinish) {
        throw new Error("Reset password failed");
      }
      
      const { success, statusCode } = response.data.resetPasswordFinish;
      
      if (!success || statusCode !== 200) {
        throw new Error("Password reset failed. Please try again.");
      }
      
      return response.data.resetPasswordFinish;
    } catch (error: any) {
      handleGraphQLError(error);
    }
  },
  
  verifyEmail: async (token: string) => {
    // Placeholder - replace with actual GraphQL mutation when available
    console.warn("verifyEmail: GraphQL mutation not implemented yet");
    throw new Error("Not implemented in GraphQL yet");
    // When implemented, will look something like:
    // const response = await client.mutate({
    //   mutation: VERIFY_EMAIL_MUTATION,
    //   variables: { token },
    // });
    // return response.data;
  },
};

// Local storage utilities
export const authStorage = {
  getToken: () => localStorage.getItem("auth-token"),
  setToken: (token: string) => localStorage.setItem("auth-token", token),
  removeToken: () => localStorage.removeItem("auth-token"),
  
  getUser: () => {
    const user = localStorage.getItem("auth-user");
    return user ? JSON.parse(user) : null;
  },
  setUser: (user: any) => localStorage.setItem("auth-user", JSON.stringify(user)),
  removeUser: () => localStorage.removeItem("auth-user"),
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!authStorage.getToken();
};

// Generate session token
export const generateSessionToken = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};